<template>
    <b-container id="company-branding" v-if="$can('view-company-branding') || $can('view-all')" fluid>
        <b-row class="page-title">
            <b-container fluid>
                <b-row>
                    <b-col cols="8">
                        <h3>Company Branding Management</h3>
                    </b-col>
                    <b-col cols="4" class="text-right">
                        <b-button variant="primary" @click="openCreateModal" class="btn-sm">
                            <i class="fas fa-plus"></i> New Company Branding
                        </b-button>
                        <b-button variant="outline-secondary" @click="openRulesModal" class="btn-sm ml-2">
                            <i class="fas fa-cogs"></i> Manage Rules
                        </b-button>
                    </b-col>
                </b-row>
                <hr />
            </b-container>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-table 
                    :items="brandings" 
                    :fields="fields" 
                    :busy="isBusy"
                    hover 
                    responsive 
                    striped
                    :per-page="perPage"
                    :current-page="currentPage"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @filtered="onFiltered"
                >
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>

                    <template v-slot:cell(logo)="row">
                        <b-img 
                            v-if="row.item.logo_path" 
                            :src="getLogoUrl(row.item.logo_path)" 
                            width="50" 
                            height="50" 
                            class="rounded"
                            thumbnail
                        ></b-img>
                        <span v-else class="text-muted">No Logo</span>
                    </template>

                    <template v-slot:cell(name)="row">
                        <strong>{{ row.item.name }}</strong>
                        <br>
                        <small class="text-muted">{{ row.item.email }}</small>
                    </template>

                    <template v-slot:cell(address)="row">
                        <div v-if="row.item.address_line1">
                            {{ row.item.address_line1 }}<br v-if="row.item.address_line2">
                            <span v-if="row.item.address_line2">{{ row.item.address_line2 }}<br></span>
                            <span v-if="row.item.city">{{ row.item.city }}, </span>
                            <span v-if="row.item.state">{{ row.item.state }} </span>
                            <span v-if="row.item.postal_code">{{ row.item.postal_code }}</span>
                            <br v-if="row.item.country">
                            <span v-if="row.item.country">{{ row.item.country }}</span>
                        </div>
                        <span v-else class="text-muted">No Address</span>
                    </template>

                    <template v-slot:cell(contact)="row">
                        <div v-if="row.item.phone">
                            <i class="fas fa-phone"></i> {{ row.item.phone }}<br>
                        </div>
                        <div v-if="row.item.website">
                            <i class="fas fa-globe"></i> 
                            <a :href="row.item.website" target="_blank" class="text-decoration-none">
                                {{ row.item.website }}
                            </a>
                        </div>
                    </template>

                    <template v-slot:cell(is_active)="row">
                        <b-badge :variant="row.item.is_active ? 'success' : 'secondary'">
                            {{ row.item.is_active ? 'Active' : 'Inactive' }}
                        </b-badge>
                    </template>

                    <template v-slot:cell(actions)="row">
                        <b-button-group size="sm">
                            <b-button variant="outline-primary" @click="editBranding(row.item)">
                                <i class="fas fa-edit"></i>
                            </b-button>
                            <b-button variant="outline-info" @click="viewRules(row.item)">
                                <i class="fas fa-cogs"></i>
                            </b-button>
                            <b-button variant="outline-danger" @click="deleteBranding(row.item)" :disabled="row.item.usage_count > 0">
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </b-button-group>
                    </template>
                </b-table>

                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="company-branding-table"
                    class="mt-3"
                ></b-pagination>
            </b-col>
        </b-row>

        <!-- Create/Edit Modal -->
        <b-modal 
            :id="modalId" 
            :title="modalTitle" 
            size="lg" 
            @hidden="resetModal"
            @ok="saveBranding"
            :ok-disabled="!isFormValid"
        >
            <b-form @submit.prevent="saveBranding">
                <b-row>
                    <b-col cols="6">
                        <b-form-group label="Company Name" label-for="name" :invalid-feedback="errors.name">
                            <b-form-input 
                                id="name" 
                                v-model="form.name" 
                                :state="errors.name ? false : null"
                                required
                            ></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6">
                        <b-form-group label="Email" label-for="email" :invalid-feedback="errors.email">
                            <b-form-input 
                                id="email" 
                                v-model="form.email" 
                                type="email"
                                :state="errors.email ? false : null"
                            ></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col cols="6">
                        <b-form-group label="Phone" label-for="phone">
                            <b-form-input id="phone" v-model="form.phone"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6">
                        <b-form-group label="Website" label-for="website">
                            <b-form-input id="website" v-model="form.website" type="url"></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-form-group label="Logo" label-for="logo">
                    <b-form-file 
                        id="logo" 
                        v-model="form.logo_file" 
                        accept="image/*"
                        @change="onLogoChange"
                    ></b-form-file>
                    <div v-if="form.logo_path" class="mt-2">
                        <b-img :src="getLogoUrl(form.logo_path)" width="100" height="100" thumbnail></b-img>
                    </div>
                </b-form-group>

                <b-row>
                    <b-col cols="6">
                        <b-form-group label="Address Line 1" label-for="address_line1">
                            <b-form-input id="address_line1" v-model="form.address_line1"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6">
                        <b-form-group label="Address Line 2" label-for="address_line2">
                            <b-form-input id="address_line2" v-model="form.address_line2"></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col cols="4">
                        <b-form-group label="City" label-for="city">
                            <b-form-input id="city" v-model="form.city"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col cols="4">
                        <b-form-group label="State" label-for="state">
                            <b-form-input id="state" v-model="form.state"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col cols="4">
                        <b-form-group label="Postal Code" label-for="postal_code">
                            <b-form-input id="postal_code" v-model="form.postal_code"></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-form-group label="Country" label-for="country">
                    <b-form-input id="country" v-model="form.country"></b-form-input>
                </b-form-group>

                <b-form-group>
                    <b-form-checkbox v-model="form.is_active" switch>
                        Active
                    </b-form-checkbox>
                </b-form-group>
            </b-form>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="closeModal">Cancel</b-button>
                <b-button variant="primary" @click="saveBranding" :disabled="!isFormValid">
                    {{ isEdit ? 'Update' : 'Create' }}
                </b-button>
            </template>
        </b-modal>

        <!-- Rules Management Modal -->
        <b-modal 
            id="rules-modal" 
            title="Branding Rules Management" 
            size="xl" 
            @hidden="resetRulesModal"
        >
            <div class="mb-3">
                <b-button variant="primary" @click="openCreateRuleModal" class="btn-sm">
                    <i class="fas fa-plus"></i> New Rule
                </b-button>
            </div>

            <b-table 
                :items="rules" 
                :fields="ruleFields" 
                hover 
                responsive 
                striped
                :busy="rulesBusy"
            >
                <template v-slot:table-busy>
                    <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Loading...</strong>
                    </div>
                </template>

                <template v-slot:cell(company_branding)="row">
                    {{ getBrandingName(row.item.company_branding_id) }}
                </template>

                <template v-slot:cell(rule_value)="row">
                    <code>{{ row.item.rule_value }}</code>
                </template>

                <template v-slot:cell(is_active)="row">
                    <b-badge :variant="row.item.is_active ? 'success' : 'secondary'">
                        {{ row.item.is_active ? 'Active' : 'Inactive' }}
                    </b-badge>
                </template>

                <template v-slot:cell(actions)="row">
                    <b-button-group size="sm">
                        <b-button variant="outline-primary" @click="editRule(row.item)">
                            <i class="fas fa-edit"></i>
                        </b-button>
                        <b-button variant="outline-danger" @click="deleteRule(row.item)">
                            <i class="fas fa-trash"></i>
                        </b-button>
                    </b-button-group>
                </template>
            </b-table>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="closeRulesModal">Close</b-button>
            </template>
        </b-modal>

        <!-- Create/Edit Rule Modal -->
        <b-modal 
            :id="ruleModalId" 
            :title="ruleModalTitle" 
            @hidden="resetRuleModal"
            @ok="saveRule"
            :ok-disabled="!isRuleFormValid"
        >
            <b-form @submit.prevent="saveRule">
                <b-form-group label="Company Branding" label-for="rule_company_branding_id" :invalid-feedback="errors.company_branding_id">
                    <b-form-select 
                        id="rule_company_branding_id" 
                        v-model="ruleForm.company_branding_id" 
                        :options="brandingOptions"
                        :state="errors.company_branding_id ? false : null"
                        required
                    ></b-form-select>
                </b-form-group>

                <b-form-group label="Rule Type" label-for="rule_type">
                    <b-form-select id="rule_type" v-model="ruleForm.rule_type" disabled>
                        <option value="prefix">Prefix Match</option>
                    </b-form-select>
                </b-form-group>

                <b-form-group label="Prefix Value" label-for="rule_value" :invalid-feedback="errors.rule_value">
                    <b-form-input 
                        id="rule_value" 
                        v-model="ruleForm.rule_value" 
                        placeholder="e.g., SHOP-, AMZ-, etc."
                        :state="errors.rule_value ? false : null"
                        required
                    ></b-form-input>
                    <b-form-text>Order numbers starting with this prefix will use the selected company branding.</b-form-text>
                </b-form-group>

                <b-form-group label="Priority" label-for="priority">
                    <b-form-input 
                        id="priority" 
                        v-model="ruleForm.priority" 
                        type="number" 
                        min="0" 
                        max="999"
                    ></b-form-input>
                    <b-form-text>Higher numbers have higher priority. Rules are checked in priority order.</b-form-text>
                </b-form-group>

                <b-form-group>
                    <b-form-checkbox v-model="ruleForm.is_active" switch>
                        Active
                    </b-form-checkbox>
                </b-form-group>

                <div class="mt-3">
                    <b-button variant="outline-info" @click="testRule" :disabled="!ruleForm.rule_value">
                        <i class="fas fa-flask"></i> Test Rule
                    </b-button>
                </div>
            </b-form>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="closeRuleModal">Cancel</b-button>
                <b-button variant="primary" @click="saveRule" :disabled="!isRuleFormValid">
                    {{ isEditRule ? 'Update' : 'Create' }}
                </b-button>
            </template>
        </b-modal>
    </b-container>
</template>

<script>
export default {
    name: 'CompanyBranding',
    
    data() {
        return {
            brandings: [],
            rules: [],
            isBusy: false,
            rulesBusy: false,
            perPage: 10,
            currentPage: 1,
            totalRows: 0,
            sortBy: 'name',
            sortDesc: false,
            
            fields: [
                { key: 'logo', label: 'Logo', sortable: false },
                { key: 'name', label: 'Company', sortable: true },
                { key: 'address', label: 'Address', sortable: false },
                { key: 'contact', label: 'Contact', sortable: false },
                { key: 'is_active', label: 'Status', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
            ],
            
            ruleFields: [
                { key: 'company_branding', label: 'Company', sortable: true },
                { key: 'rule_type', label: 'Type', sortable: true },
                { key: 'rule_value', label: 'Prefix', sortable: true },
                { key: 'priority', label: 'Priority', sortable: true },
                { key: 'is_active', label: 'Status', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
            ],
            
            // Modal states
            modalId: 'branding-modal',
            ruleModalId: 'rule-modal',
            isEdit: false,
            isEditRule: false,
            
            // Forms
            form: {
                id: null,
                name: '',
                email: '',
                phone: '',
                website: '',
                logo_file: null,
                logo_path: '',
                address_line1: '',
                address_line2: '',
                city: '',
                state: '',
                postal_code: '',
                country: '',
                is_active: true
            },
            
            ruleForm: {
                id: null,
                company_branding_id: null,
                rule_type: 'prefix',
                rule_value: '',
                priority: 0,
                is_active: true
            },
            
            errors: {},
            brandingOptions: []
        }
    },
    
    computed: {
        modalTitle() {
            return this.isEdit ? 'Edit Company Branding' : 'Create Company Branding'
        },
        
        ruleModalTitle() {
            return this.isEditRule ? 'Edit Branding Rule' : 'Create Branding Rule'
        },
        
        isFormValid() {
            return this.form.name && this.form.email
        },
        
        isRuleFormValid() {
            return this.ruleForm.company_branding_id && this.ruleForm.rule_value
        }
    },
    
    methods: {
        async loadBrandings() {
            this.isBusy = true
            try {
                const response = await axios.get('/api/company/branding')
                if (response.data.status === 'success') {
                    this.brandings = response.data.brandings
                    this.totalRows = this.brandings.length
                    this.brandingOptions = this.brandings.map(b => ({
                        value: b.id,
                        text: b.name
                    }))
                }
            } catch (error) {
                this.showAlert('error', 'Error loading brandings', error.response?.data?.message || 'Unknown error')
            } finally {
                this.isBusy = false
            }
        },
        
        async loadRules() {
            this.rulesBusy = true
            try {
                const response = await axios.get('/api/company/branding-rules')
                if (response.data.status === 'success') {
                    this.rules = response.data.rules
                }
            } catch (error) {
                this.showAlert('error', 'Error loading rules', error.response?.data?.message || 'Unknown error')
            } finally {
                this.rulesBusy = false
            }
        },
        
        getLogoUrl(logoPath) {
            return logoPath ? `/storage/${logoPath}` : null
        },
        
        getBrandingName(brandingId) {
            const branding = this.brandings.find(b => b.id === brandingId)
            return branding ? branding.name : 'Unknown'
        },
        
        openCreateModal() {
            this.isEdit = false
            this.resetForm()
            this.$bvModal.show(this.modalId)
        },
        
        editBranding(branding) {
            this.isEdit = true
            this.form = { ...branding }
            this.$bvModal.show(this.modalId)
        },
        
        async saveBranding() {
            this.errors = {}
            
            try {
                const formData = new FormData()
                Object.keys(this.form).forEach(key => {
                    if (key !== 'logo_file' && this.form[key] !== null) {
                        formData.append(key, this.form[key])
                    }
                })
                
                if (this.form.logo_file) {
                    formData.append('logo', this.form.logo_file)
                }
                
                // For update requests, use POST with _method override since PUT doesn't work well with FormData
                if (this.isEdit) {
                    formData.append('_method', 'PUT')
                }
                
                const url = this.isEdit ? `/api/company/branding/${this.form.id}` : '/api/company/branding'
                
                const response = await axios.post(url, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                })
                
                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    this.closeModal()
                    this.loadBrandings()
                }
            } catch (error) {
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors
                } else {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },
        
        async deleteBranding(branding) {
            if (branding.usage_count > 0) {
                this.showAlert('error', 'Cannot Delete', 'This branding is being used by sales orders')
                return
            }
            
            if (confirm(`Are you sure you want to delete "${branding.name}"?`)) {
                try {
                    const response = await axios.delete(`/api/company/branding/${branding.id}`)
                    if (response.data.status === 'success') {
                        this.showAlert('success', 'Success', response.data.message)
                        this.loadBrandings()
                    }
                } catch (error) {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },
        
        openRulesModal() {
            this.loadRules()
            this.$bvModal.show('rules-modal')
        },
        
        openCreateRuleModal() {
            this.isEditRule = false
            this.resetRuleForm()
            this.$bvModal.show(this.ruleModalId)
        },
        
        editRule(rule) {
            this.isEditRule = true
            this.ruleForm = { ...rule }
            this.$bvModal.show(this.ruleModalId)
        },
        
        async saveRule() {
            this.errors = {}
            
            try {
                const url = this.isEditRule ? `/api/company/branding-rules/${this.ruleForm.id}` : '/api/company/branding-rules'
                const method = this.isEditRule ? 'put' : 'post'
                
                const response = await axios[method](url, this.ruleForm)
                
                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    this.closeRuleModal()
                    this.loadRules()
                }
            } catch (error) {
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors
                } else {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },
        
        async deleteRule(rule) {
            if (confirm(`Are you sure you want to delete this rule?`)) {
                try {
                    const response = await axios.delete(`/api/company/branding-rules/${rule.id}`)
                    if (response.data.status === 'success') {
                        this.showAlert('success', 'Success', response.data.message)
                        this.loadRules()
                    }
                } catch (error) {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },
        
        async testRule() {
            if (!this.ruleForm.rule_value) return
            
            try {
                const response = await axios.post('/api/company/branding-rules/test-rule', {
                    rule_value: this.ruleForm.rule_value,
                    order_number: 'TEST-' + this.ruleForm.rule_value + '123'
                })
                
                if (response.data.status === 'success') {
                    const message = response.data.message
                    this.showAlert('info', 'Rule Test', message)
                }
            } catch (error) {
                this.showAlert('error', 'Test Error', error.response?.data?.message || 'Unknown error')
            }
        },
        
        viewRules(branding) {
            // Filter rules for this branding
            this.rules = this.rules.filter(rule => rule.company_branding_id === branding.id)
            this.openRulesModal()
        },
        
        onLogoChange(event) {
            const file = event.target.files[0]
            if (file) {
                this.form.logo_file = file
            }
        },
        
        resetForm() {
            this.form = {
                id: null,
                name: '',
                email: '',
                phone: '',
                website: '',
                logo_file: null,
                logo_path: '',
                address_line1: '',
                address_line2: '',
                city: '',
                state: '',
                postal_code: '',
                country: '',
                is_active: true
            }
            this.errors = {}
        },
        
        resetRuleForm() {
            this.ruleForm = {
                id: null,
                company_branding_id: null,
                rule_type: 'prefix',
                rule_value: '',
                priority: 0,
                is_active: true
            }
            this.errors = {}
        },
        
        closeModal() {
            this.$bvModal.hide(this.modalId)
            this.resetForm()
        },
        
        closeRulesModal() {
            this.$bvModal.hide('rules-modal')
        },
        
        closeRuleModal() {
            this.$bvModal.hide(this.ruleModalId)
            this.resetRuleForm()
        },
        
        resetModal() {
            this.resetForm()
        },
        
        resetRulesModal() {
            this.loadRules()
        },
        
        resetRuleModal() {
            this.resetRuleForm()
        },
        
        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length
            this.currentPage = 1
        },
        
        showAlert(type, title, message) {
            this.$bvToast.toast(message, {
                title: title,
                variant: type,
                solid: true,
                autoHideDelay: 5000
            })
        }
    },
    
    mounted() {
        this.loadBrandings()
    }
}
</script>

<style scoped>
#company-branding {
    margin-top: 80px;
}

.page-title {
    margin-bottom: 20px;
}

.table th {
    border-top: none;
}

.badge {
    font-size: 0.75em;
}

.btn-group .btn {
    margin-right: 2px;
}

.btn-group .btn:last-child {
    margin-right: 0;
}
</style>
